import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import billyLogo from "@/assets/logo-wt-bg.png";
import billyLogoSecondary from "@/assets/logo-secondary.png";
import { motion } from "framer-motion";
import { 
  ShoppingCart, 
  Zap, 
  CheckCircle, 
  Tag, 
  Receipt, 
  Copy,
  Wallet, 
  BarChart3, 
  Target, 
  Calendar, 
  Globe, 
  Menu, 
  X, 
  Play, 
  Apple, 
  Check, 
  Minus,
  Instagram,
  Mail,
  FileDown,
  MessageCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription 
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Switch } from "@/components/ui/switch";
import { Toaster, toast } from "@/components/ui/sonner";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// ── i18n ─────────────────────────────────────────────────────────────────────
const translations = {
  pt: {
    nav: {
      features: "Funcionalidades",
      howItWorks: "Como Funciona",
      plans: "Planos",
      download: "Download",
      faq: "FAQ",
    },
    hero: {
      slogan: "Listas inteligentes. Gastos sob controle. Vida organizada.",
      subtitle: "Billy é o app que une listas de compras colaborativas em tempo real com controle completo de despesas para você, sua família e seus amigos.",
    },
    features: {
      shoppingTitle: "Listas de Compras em Tempo Real",
      shoppingSubtitle: "Colabore com quem você ama, de onde estiver",
      expenseTitle: "Controle de Despesas Inteligente",
      expenseSubtitle: "Saiba exatamente para onde vai cada centavo",
      shopping: [
        { title: "Listas Compartilhadas", description: "Crie listas e compartilhe com amigos e família. Todos veem as atualizações instantaneamente." },
        { title: "Sincronização em Tempo Real", description: "Quando alguém adiciona ou risca um item, todos os participantes veem na hora sem precisar atualizar." },
        { title: "Marque como Comprado", description: "Risque itens enquanto faz as compras. A lista atualiza para todos ao mesmo tempo." },
        { title: "Tags e Categorias", description: "Organize suas listas com tags coloridas (até 5 por lista)." },
        { title: "Comprovantes de Compra", description: "Fotografe recibos e guarde diretamente na lista." },
        { title: "Duplicar Lista", description: "Repita listas que você já usou antes com um toque, ideal para compras recorrentes." }
      ],
      expense: [
        { title: "Registro de Gastos", description: "Registre receitas e despesas por categoria com poucos toques." },
        { title: "Dashboard Visual", description: "Gráficos claros que mostram seus hábitos de consumo por período." },
        { title: "Meta Mensal", description: "Defina quanto quer gastar no mês e acompanhe em tempo real." },
        { title: "Comparativo de Períodos", description: "Compare seus gastos entre meses e identifique onde gasta mais." },
        { title: "Multilíngue", description: "Disponível em Português e Inglês." },
        { title: "Exportar PDF / CSV", description: "Exporte qualquer lista como PDF ou CSV para melhor controle das despesas, guarde para seu histórico ou compartilhe onde quiser." }
      ]
    },
    howItWorks: {
      title: "Como Funciona",
      tabExpenses: "Controle de Despesas",
      tabShopping: "Listas de Compras",
      expenseSteps: [
        { title: "Crie sua conta", description: "Cadastre-se gratuitamente no Billy" },
        { title: "Registre movimentações", description: "Adicione suas receitas e despesas por categoria" },
        { title: "Acompanhe seu progresso", description: "Visualize seu dashboard e bata sua meta mensal" }
      ],
      shoppingSteps: [
        { title: "Crie uma lista", description: "Dê um nome para sua lista (ex: \"Mercado da semana\")" },
        { title: "Convide participantes", description: "Adicione amigos ou família pelo app" },
        { title: "Colabore em tempo real", description: "Adicionem itens juntos. Cada atualização aparece em tempo real para todos" }
      ]
    },
    pricing: {
      title: "Escolha o plano ideal para você",
      subtitle: "Comece grátis e evolua conforme sua necessidade",
      monthly: "Mensal",
      annual: "Anual",
      saveBadge: "Economize até 37%",
      startFree: "Começar Grátis",
      subscribe: "Assinar Agora",
      plans: [
        {
          name: "Free",
          price: { monthly: "Grátis", annual: "Grátis" },
          badge: null,
          savingsText: null,
          isPro: false,
          features: [
            { text: "Listas de compras ilimitadas", included: true },
            { text: "Itens por lista ilimitados", included: true },
            { text: "Compartilhamento em tempo real", included: true },
            { text: "Controle básico de gastos", included: true },
            { text: "Dashboard básico", included: true },
            { text: "Comprovantes de compra", included: false },
            { text: "Tags e categorias", included: false },
            { text: "Meta mensal", included: false },
            { text: "Comparativo de períodos", included: false }
          ]
        },
        {
          name: "Essential",
          price: { monthly: "R$ 9,90/mês", annual: "R$ 79,90/ano" },
          badge: { text: "Popular", color: "bg-[#059669]" },
          savingsText: "economia de 33%",
          isPro: false,
          features: [
            { text: "Tudo do Free", included: true },
            { text: "Comprovantes de compra", included: true },
            { text: "Relatórios avançados", included: true },
            { text: "Suporte por e-mail", included: true },
            { text: "Tags avançadas", included: false },
            { text: "Meta mensal", included: false },
            { text: "Comparativo de períodos", included: false }
          ]
        },
        {
          name: "Pro",
          price: { monthly: "R$ 19,90/mês", annual: "R$ 149,90/ano" },
          badge: { text: "Melhor custo-benefício", color: "bg-[#4F46E5]" },
          savingsText: "economia de 37%",
          isPro: true,
          features: [
            { text: "Tudo do Essential", included: true },
            { text: "Tags e categorias avançadas (até 5 por lista)", included: true },
            { text: "Meta mensal personalizada", included: true },
            { text: "Comparativo entre períodos", included: true },
            { text: "Suporte prioritário", included: true },
            { text: "Acesso antecipado a novidades", included: true }
          ]
        }
      ]
    },
    faq: {
      title: "Como podemos ajudar?",
      subtitle: "Encontre respostas rápidas abaixo.",
      notFound: "Não encontrou o que precisava?",
      emailSupport: "Enviar e-mail para o suporte",
      items: [
        { question: "Como criar uma lista de despesas?", answer: "Na tela inicial, toque no botão \"+\" para criar uma nova lista. Dê um nome, escolha uma categoria e comece a adicionar itens com preço e quantidade." },
        { question: "Como compartilhar uma lista com alguém?", answer: "Abra a lista desejada, toque no ícone de compartilhar e informe o e-mail do usuário Billy com quem deseja compartilhar. Ele receberá acesso imediato." },
        { question: "Qual a diferença entre os planos FREE, ESSENTIAL e PRO?", answer: "FREE: até 3 despesas, compartilhar com 1 pessoa, histórico de 3 meses.\n\nESSENTIAL: despesas ilimitadas, compartilhar com 4 pessoas, comprovantes, histórico de 1 ano.\n\nPRO: tudo do ESSENTIAL + compartilhamento ilimitado, histórico ilimitado, metas mensais e analytics avançados." },
        { question: "Como fazer upgrade do meu plano?", answer: "Vá em Perfil → botão \"Fazer upgrade\". Escolha o plano desejado e conclua o pagamento pela App Store ou Google Play." },
        { question: "Esqueci minha senha. Como recuperar?", answer: "Na tela de login, toque em \"Esqueci minha senha\". Informe seu e-mail e você receberá um código de 6 dígitos para criar uma nova senha." },
        { question: "Como adicionar um comprovante a uma despesa?", answer: "Abra a lista e toque no ícone de comprovante ao lado do item. Você pode tirar uma foto ou selecionar uma imagem da galeria. Disponível nos planos ESSENTIAL e PRO." },
        { question: "Posso excluir minha conta?", answer: "Sim. Vá em Perfil → Configurações → Zona de perigo → Excluir conta. Você precisará confirmar o motivo e um código enviado por e-mail. A ação é irreversível." },
        { question: "Meus dados ficam seguros?", answer: "Sim. Todos os dados são armazenados com criptografia e transmitidos via HTTPS. Senhas são protegidas com hash bcrypt. Consulte nossa Política de Privacidade para mais detalhes." },
        { question: "O Billy funciona sem internet?", answer: "No momento o Billy requer conexão com a internet para sincronizar dados em tempo real. Suporte offline está planejado para versões futuras." },
        { question: "Como cancelar minha assinatura?", answer: "As assinaturas são gerenciadas pela App Store (iOS) ou Google Play (Android). Acesse as configurações da loja, vá em Assinaturas e cancele o plano Billy." }
      ]
    },
    download: {
      title: "Disponível agora no iOS!",
      subtitle: "Baixe já na App Store. Android em breve — cadastre-se para ser notificado.",
    },
    footer: {
      description: "O app que une listas de compras colaborativas em tempo real com controle completo de despesas.",
      links: "Links",
      privacy: "Política de Privacidade",
      terms: "Termos de Uso",
      contact: "Contato",
      contacts: "Contatos",
      copyright: "© 2026 Billy Tecnologia Ltda. Todos os direitos reservados.",
    },
    modals: {
      playStore: {
        title: "Em breve na Google Play",
        description: "Cadastre seu e-mail para ser avisado quando o Billy estiver disponível!",
        placeholder: "seu@email.com",
        button: "Quero ser avisado",
        successMessage: "E-mail cadastrado com sucesso! Você será avisado quando o app estiver disponível.",
      },
      subscribe: {
        title: "Assine pelo app",
        description: "As assinaturas são gerenciadas pelas lojas. Baixe o Billy e assine no app em Perfil → Fazer Upgrade.",
        iosButton: "Baixar na App Store",
        androidLabel: "Android — em breve",
        placeholder: "seu@email.com",
        notifyButton: "Me avise",
        successMessage: "E-mail cadastrado! Você será avisado quando o app estiver disponível no Android.",
      },
    }
  },
  en: {
    nav: {
      features: "Features",
      howItWorks: "How It Works",
      plans: "Plans",
      download: "Download",
      faq: "FAQ",
    },
    hero: {
      slogan: "Smart lists. Expenses under control. Organized life.",
      subtitle: "Billy is the app that combines real-time collaborative shopping lists with complete expense control for you, your family and your friends.",
    },
    features: {
      shoppingTitle: "Real-Time Shopping Lists",
      shoppingSubtitle: "Collaborate with those you love, from anywhere",
      expenseTitle: "Smart Expense Control",
      expenseSubtitle: "Know exactly where every cent goes",
      shopping: [
        { title: "Shared Lists", description: "Create lists and share with friends and family. Everyone sees updates instantly." },
        { title: "Real-Time Sync", description: "When someone adds or crosses off an item, all participants see it immediately without refreshing." },
        { title: "Mark as Purchased", description: "Cross off items as you shop. The list updates for everyone at the same time." },
        { title: "Tags & Categories", description: "Organize your lists with colorful tags (up to 5 per list)." },
        { title: "Purchase Receipts", description: "Photograph receipts and store them directly in the list." },
        { title: "Duplicate List", description: "Repeat lists you've used before with one tap, ideal for recurring purchases." }
      ],
      expense: [
        { title: "Expense Tracking", description: "Record income and expenses by category with just a few taps." },
        { title: "Visual Dashboard", description: "Clear charts showing your spending habits by period." },
        { title: "Monthly Budget", description: "Set how much you want to spend per month and track it in real time." },
        { title: "Period Comparison", description: "Compare your spending between months and identify where you spend the most." },
        { title: "Multilingual", description: "Available in Portuguese and English." },
        { title: "Export PDF / CSV", description: "Export any list as PDF or CSV for better expense control, keep for your history or share wherever you want." }
      ]
    },
    howItWorks: {
      title: "How It Works",
      tabExpenses: "Expense Control",
      tabShopping: "Shopping Lists",
      expenseSteps: [
        { title: "Create your account", description: "Sign up for free on Billy" },
        { title: "Record transactions", description: "Add your income and expenses by category" },
        { title: "Track your progress", description: "View your dashboard and hit your monthly goal" }
      ],
      shoppingSteps: [
        { title: "Create a list", description: "Give your list a name (e.g. \"Weekly groceries\")" },
        { title: "Invite participants", description: "Add friends or family through the app" },
        { title: "Collaborate in real time", description: "Add items together. Every update appears in real time for everyone" }
      ]
    },
    pricing: {
      title: "Choose the ideal plan for you",
      subtitle: "Start free and upgrade as you need",
      monthly: "Monthly",
      annual: "Annual",
      saveBadge: "Save up to 37%",
      startFree: "Get Started Free",
      subscribe: "Subscribe Now",
      plans: [
        {
          name: "Free",
          price: { monthly: "Free", annual: "Free" },
          badge: null,
          savingsText: null,
          isPro: false,
          features: [
            { text: "Unlimited shopping lists", included: true },
            { text: "Unlimited items per list", included: true },
            { text: "Real-time sharing", included: true },
            { text: "Basic expense control", included: true },
            { text: "Basic dashboard", included: true },
            { text: "Purchase receipts", included: false },
            { text: "Tags & categories", included: false },
            { text: "Monthly budget", included: false },
            { text: "Period comparison", included: false }
          ]
        },
        {
          name: "Essential",
          price: { monthly: "R$ 9,90/mo", annual: "R$ 79,90/yr" },
          badge: { text: "Popular", color: "bg-[#059669]" },
          savingsText: "save 33%",
          isPro: false,
          features: [
            { text: "Everything in Free", included: true },
            { text: "Purchase receipts", included: true },
            { text: "Advanced reports", included: true },
            { text: "Email support", included: true },
            { text: "Advanced tags", included: false },
            { text: "Monthly budget", included: false },
            { text: "Period comparison", included: false }
          ]
        },
        {
          name: "Pro",
          price: { monthly: "R$ 19,90/mo", annual: "R$ 149,90/yr" },
          badge: { text: "Best value", color: "bg-[#4F46E5]" },
          savingsText: "save 37%",
          isPro: true,
          features: [
            { text: "Everything in Essential", included: true },
            { text: "Advanced tags & categories (up to 5 per list)", included: true },
            { text: "Custom monthly budget", included: true },
            { text: "Period comparison", included: true },
            { text: "Priority support", included: true },
            { text: "Early access to new features", included: true }
          ]
        }
      ]
    },
    faq: {
      title: "How can we help?",
      subtitle: "Find quick answers below.",
      notFound: "Didn't find what you were looking for?",
      emailSupport: "Send email to support",
      items: [
        { question: "How do I create an expense list?", answer: "On the home screen, tap the \"+\" button to create a new list. Give it a name, choose a category and start adding items with price and quantity." },
        { question: "How do I share a list with someone?", answer: "Open the desired list, tap the share icon and enter the Billy user's email you want to share with. They'll get instant access." },
        { question: "What's the difference between FREE, ESSENTIAL, and PRO plans?", answer: "FREE: up to 3 expenses, share with 1 person, 3-month history.\n\nESSENTIAL: unlimited expenses, share with 4 people, receipts, 1-year history.\n\nPRO: everything in ESSENTIAL + unlimited sharing, unlimited history, monthly goals and advanced analytics." },
        { question: "How do I upgrade my plan?", answer: "Go to Profile → \"Upgrade\" button. Choose your desired plan and complete payment through the App Store or Google Play." },
        { question: "I forgot my password. How do I recover it?", answer: "On the login screen, tap \"Forgot my password\". Enter your email and you'll receive a 6-digit code to create a new password." },
        { question: "How do I add a receipt to an expense?", answer: "Open the list and tap the receipt icon next to the item. You can take a photo or select an image from your gallery. Available on ESSENTIAL and PRO plans." },
        { question: "Can I delete my account?", answer: "Yes. Go to Profile → Settings → Danger Zone → Delete account. You'll need to confirm the reason and a code sent by email. The action is irreversible." },
        { question: "Is my data safe?", answer: "Yes. All data is stored with encryption and transmitted via HTTPS. Passwords are protected with bcrypt hash. See our Privacy Policy for more details." },
        { question: "Does Billy work offline?", answer: "Currently Billy requires an internet connection to sync data in real time. Offline support is planned for future versions." },
        { question: "How do I cancel my subscription?", answer: "Subscriptions are managed by the App Store (iOS) or Google Play (Android). Go to the store settings, navigate to Subscriptions and cancel the Billy plan." }
      ]
    },
    download: {
      title: "Now available on iOS!",
      subtitle: "Download now on the App Store. Android coming soon — sign up to be notified.",
    },
    footer: {
      description: "The app that combines real-time collaborative shopping lists with complete expense control.",
      links: "Links",
      privacy: "Privacy Policy",
      terms: "Terms of Service",
      contact: "Contact",
      contacts: "Contact Us",
      copyright: "© 2026 Billy Tecnologia Ltda. All rights reserved.",
    },
    modals: {
      playStore: {
        title: "Coming soon to Google Play",
        description: "Sign up with your email to be notified when Billy is available!",
        placeholder: "your@email.com",
        button: "Notify me",
        successMessage: "Email registered! You'll be notified when the app is available.",
      },
      subscribe: {
        title: "Subscribe in the app",
        description: "Subscriptions are managed by the app stores. Download Billy and subscribe in the app at Profile → Upgrade.",
        iosButton: "Download on the App Store",
        androidLabel: "Android — coming soon",
        placeholder: "your@email.com",
        notifyButton: "Notify me",
        successMessage: "Email registered! You'll be notified when the app is available on Android.",
      },
    }
  }
};

const LangContext = createContext(null);
const useLang = () => useContext(LangContext);

// Navbar Component
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { lang, setLang, t } = useLang();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#funcionalidades", label: t.nav.features },
    { href: "#como-funciona", label: t.nav.howItWorks },
    { href: "#planos", label: t.nav.plans },
    { href: "#download", label: t.nav.download },
    { href: "#faq", label: t.nav.faq }
  ];

  return (
    <>
      {/* Backdrop overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <nav
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || isMobileMenuOpen
            ? "bg-white/95 backdrop-blur-md border-b border-gray-200/50 shadow-sm"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <a href="#" className="flex items-center gap-2" data-testid="logo">
              <img src={isScrolled || isMobileMenuOpen ? billyLogoSecondary : billyLogo} alt="Billy" className="h-14 object-contain" />
            </a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className={`navbar-link text-sm font-medium ${isScrolled ? '' : 'navbar-link-white'}`}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(" ", "-")}`}
                >
                  {link.label}
                </a>
              ))}
              <div
                className={`ml-3 flex items-center rounded-full border p-0.5 text-sm font-medium transition-all ${
                  isScrolled ? "border-gray-200 bg-gray-50" : "border-white/30 bg-white/10"
                }`}
                aria-label="Select language"
              >
                <button
                  onClick={() => setLang("pt")}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full transition-all duration-200 ${
                    lang === "pt"
                      ? "bg-[#4F46E5] text-white shadow-sm"
                      : isScrolled ? "text-gray-500 hover:text-gray-700" : "text-white/70 hover:text-white"
                  }`}
                >
                  <span>🇧🇷</span>
                  <span>PT</span>
                </button>
                <button
                  onClick={() => setLang("en")}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full transition-all duration-200 ${
                    lang === "en"
                      ? "bg-[#4F46E5] text-white shadow-sm"
                      : isScrolled ? "text-gray-500 hover:text-gray-700" : "text-white/70 hover:text-white"
                  }`}
                >
                  <span>EN</span>
                  <span>🇺🇸</span>
                </button>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              className={`md:hidden p-2 rounded-lg transition-colors ${
                isScrolled || isMobileMenuOpen
                  ? "text-gray-700 hover:bg-gray-100"
                  : "text-white hover:bg-white/10"
              }`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-button"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        <div
          className={`mobile-menu md:hidden border-t border-gray-100 ${
            isMobileMenuOpen ? "open" : ""
          }`}
          data-testid="mobile-menu"
        >
          <div className="bg-white px-4 py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-gray-700 font-medium px-4 py-3 rounded-xl hover:text-[#4F46E5] hover:bg-indigo-50 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <div className="flex items-center gap-3 px-4 py-3 mt-1 border-t border-gray-100">
              <span className="text-gray-500 text-sm font-medium">{lang === 'pt' ? 'Idioma' : 'Language'}</span>
              <div className="flex items-center rounded-full border border-gray-200 bg-gray-50 p-0.5 text-sm font-medium">
                <button
                  onClick={() => { setLang("pt"); setIsMobileMenuOpen(false); }}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full transition-all duration-200 ${
                    lang === "pt" ? "bg-[#4F46E5] text-white shadow-sm" : "text-gray-500"
                  }`}
                >
                  <span>🇧🇷</span>
                  <span>PT</span>
                </button>
                <button
                  onClick={() => { setLang("en"); setIsMobileMenuOpen(false); }}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full transition-all duration-200 ${
                    lang === "en" ? "bg-[#4F46E5] text-white shadow-sm" : "text-gray-500"
                  }`}
                >
                  <span>EN</span>
                  <span>🇺🇸</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

// Hero Section
const HeroSection = ({ onPlayClick, onAppleClick }) => {
  const { t } = useLang();
  return (
    <section 
      className="min-h-screen hero-gradient relative overflow-hidden pt-16"
      data-testid="hero-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-white text-center lg:text-left"
          >
            <motion.h1
              variants={fadeInUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6"
              style={{ fontFamily: 'Poppins, sans-serif' }}
              data-testid="hero-title"
            >
              Billy
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-xl sm:text-2xl font-semibold mb-4 opacity-95"
              data-testid="hero-slogan"
            >
              {t.hero.slogan}
            </motion.p>
            <motion.p
              variants={fadeInUp}
              className="text-base sm:text-lg opacity-90 mb-8 max-w-xl mx-auto lg:mx-0"
              data-testid="hero-subtitle"
            >
              {t.hero.subtitle}
            </motion.p>
            
            {/* Store Buttons */}
            <motion.div
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <button
                onClick={onPlayClick}
                className="store-button store-button-play btn-scale"
                data-testid="hero-play-store-btn"
              >
                <Play className="w-6 h-6" />
                <span>Google Play</span>
              </button>
              <button
                onClick={onAppleClick}
                className="store-button bg-white text-[#4F46E5] btn-scale"
                data-testid="hero-app-store-btn"
              >
                <Apple className="w-6 h-6" />
                <span>App Store</span>
              </button>
            </motion.div>
          </motion.div>

          {/* Phone Mockup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex justify-center lg:justify-end"
          >
            <div className="phone-mockup animate-float" data-testid="phone-mockup">
              <div className="phone-screen">
                {/* App Header */}
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <span className="text-gray-500" style={{ fontSize: 8 }}>←</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 font-bold truncate" style={{ fontSize: 11, fontFamily: 'Poppins, sans-serif' }}>Café da manhã (12/02)</p>
                    <p className="text-gray-400" style={{ fontSize: 9 }}>3 de 5 itens</p>
                  </div>
                  <div className="flex gap-1">
                    <div className="w-4 h-4 rounded bg-gray-100 flex items-center justify-center"><span style={{ fontSize: 8 }}>⋮</span></div>
                    <div className="w-4 h-4 rounded bg-gray-100 flex items-center justify-center"><span style={{ fontSize: 8 }}>↑</span></div>
                  </div>
                </div>

                {/* Tag */}
                <div className="mb-3">
                  <span className="border border-[#4F46E5] text-[#4F46E5] rounded-full px-2 py-0.5" style={{ fontSize: 9 }}>● Mercado</span>
                </div>

                {/* List Items */}
                <div className="space-y-2 flex-1 overflow-y-auto" style={{ maxHeight: 340 }}>
                  {/* Item checked - Leite Integral */}
                  <div className="bg-white rounded-xl p-2 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-[#059669] flex items-center justify-center flex-shrink-0">
                        <span className="text-white" style={{ fontSize: 8 }}>✓</span>
                      </div>
                      <span className="text-gray-400 line-through flex-1" style={{ fontSize: 10 }}>Leite Integral</span>
                      <div className="flex gap-1">
                        <div className="w-5 h-5 rounded-lg bg-indigo-50 flex items-center justify-center"><span className="text-[#4F46E5]" style={{ fontSize: 8 }}>✎</span></div>
                        <div className="w-5 h-5 rounded-lg bg-red-50 flex items-center justify-center"><span className="text-red-400" style={{ fontSize: 8 }}>🗑</span></div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 mt-1 ml-7">
                      <span className="bg-indigo-50 text-[#4F46E5] rounded px-1" style={{ fontSize: 8 }}>□ 1x</span>
                      <span className="border border-gray-200 rounded px-1 text-gray-600" style={{ fontSize: 8 }}>R$ 7,89</span>
                      <span className="text-gray-400" style={{ fontSize: 8 }}>Total:</span>
                      <span className="text-[#4F46E5] font-bold" style={{ fontSize: 8 }}>R$ 7,89</span>
                    </div>
                  </div>

                  {/* Item checked - Pão francês */}
                  <div className="bg-white rounded-xl p-2 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-[#059669] flex items-center justify-center flex-shrink-0">
                        <span className="text-white" style={{ fontSize: 8 }}>✓</span>
                      </div>
                      <span className="text-gray-400 line-through flex-1" style={{ fontSize: 10 }}>Pão francês</span>
                      <div className="flex gap-1">
                        <div className="w-5 h-5 rounded-lg bg-indigo-50 flex items-center justify-center"><span className="text-[#4F46E5]" style={{ fontSize: 8 }}>✎</span></div>
                        <div className="w-5 h-5 rounded-lg bg-red-50 flex items-center justify-center"><span className="text-red-400" style={{ fontSize: 8 }}>🗑</span></div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 mt-1 ml-7">
                      <span className="bg-indigo-50 text-[#4F46E5] rounded px-1" style={{ fontSize: 8 }}>□ 6x</span>
                      <span className="border border-gray-200 rounded px-1 text-gray-600" style={{ fontSize: 8 }}>R$ 0,89</span>
                      <span className="text-gray-400" style={{ fontSize: 8 }}>Total:</span>
                      <span className="text-[#4F46E5] font-bold" style={{ fontSize: 8 }}>R$ 5,34</span>
                    </div>
                  </div>

                  {/* Item pending - Manteiga */}
                  <div className="bg-white rounded-xl p-2 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0" />
                      <span className="text-gray-800 font-medium flex-1" style={{ fontSize: 10 }}>Manteiga</span>
                      <div className="flex gap-1">
                        <div className="w-5 h-5 rounded-lg bg-indigo-50 flex items-center justify-center"><span className="text-[#4F46E5]" style={{ fontSize: 8 }}>✎</span></div>
                        <div className="w-5 h-5 rounded-lg bg-red-50 flex items-center justify-center"><span className="text-red-400" style={{ fontSize: 8 }}>🗑</span></div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 mt-1 ml-7">
                      <span className="bg-indigo-50 text-[#4F46E5] rounded px-1" style={{ fontSize: 8 }}>□ 1x</span>
                      <span className="border border-gray-200 rounded px-1 text-gray-600" style={{ fontSize: 8 }}>R$ 8,90</span>
                      <span className="text-gray-400" style={{ fontSize: 8 }}>Total:</span>
                      <span className="text-[#4F46E5] font-bold" style={{ fontSize: 8 }}>R$ 8,90</span>
                    </div>
                  </div>

                  {/* Item checked - Iogurte */}
                  <div className="bg-white rounded-xl p-2 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-[#059669] flex items-center justify-center flex-shrink-0">
                        <span className="text-white" style={{ fontSize: 8 }}>✓</span>
                      </div>
                      <span className="text-gray-400 line-through flex-1" style={{ fontSize: 10 }}>Iogurte Natural</span>
                      <div className="flex gap-1">
                        <div className="w-5 h-5 rounded-lg bg-indigo-50 flex items-center justify-center"><span className="text-[#4F46E5]" style={{ fontSize: 8 }}>✎</span></div>
                        <div className="w-5 h-5 rounded-lg bg-red-50 flex items-center justify-center"><span className="text-red-400" style={{ fontSize: 8 }}>🗑</span></div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 mt-1 ml-7">
                      <span className="bg-indigo-50 text-[#4F46E5] rounded px-1" style={{ fontSize: 8 }}>□ 4x</span>
                      <span className="border border-gray-200 rounded px-1 text-gray-600" style={{ fontSize: 8 }}>R$ 2,49</span>
                      <span className="text-gray-400" style={{ fontSize: 8 }}>Total:</span>
                      <span className="text-[#4F46E5] font-bold" style={{ fontSize: 8 }}>R$ 9,96</span>
                    </div>
                  </div>

                  {/* Item pending - Café */}
                  <div className="bg-white rounded-xl p-2 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0" />
                      <span className="text-gray-800 font-medium flex-1" style={{ fontSize: 10 }}>Café</span>
                      <div className="flex gap-1">
                        <div className="w-5 h-5 rounded-lg bg-indigo-50 flex items-center justify-center"><span className="text-[#4F46E5]" style={{ fontSize: 8 }}>✎</span></div>
                        <div className="w-5 h-5 rounded-lg bg-red-50 flex items-center justify-center"><span className="text-red-400" style={{ fontSize: 8 }}>🗑</span></div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 mt-1 ml-7">
                      <span className="bg-indigo-50 text-[#4F46E5] rounded px-1" style={{ fontSize: 8 }}>□ 1x</span>
                      <span className="border border-gray-200 rounded px-1 text-gray-600" style={{ fontSize: 8 }}>R$ 14,50</span>
                      <span className="text-gray-400" style={{ fontSize: 8 }}>Total:</span>
                      <span className="text-[#4F46E5] font-bold" style={{ fontSize: 8 }}>R$ 14,50</span>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-800 font-bold" style={{ fontSize: 11 }}>Total:</span>
                    <span className="text-[#059669] font-bold" style={{ fontSize: 13 }}>R$ 47,59</span>
                  </div>
                  <div className="bg-[#059669] rounded-xl py-2 flex items-center justify-center gap-1">
                    <span className="text-white" style={{ fontSize: 9 }}>✓✓</span>
                    <span className="text-white font-semibold" style={{ fontSize: 10, fontFamily: 'Poppins, sans-serif' }}>Finalizar Despesa</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Decorative elements */}
      <div className="absolute top-1/4 left-10 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-10 w-96 h-96 bg-white/5 rounded-full blur-3xl pointer-events-none" />
    </section>
  );
};

// Features Section
const FeaturesSection = () => {
  const { t } = useLang();
  const shoppingIcons = [ShoppingCart, Zap, CheckCircle, Tag, Receipt, Copy];
  const expenseIcons = [Wallet, BarChart3, Target, Calendar, Globe, FileDown];
  const shoppingFeatures = t.features.shopping.map((f, i) => ({ ...f, icon: shoppingIcons[i] }));
  const expenseFeatures = t.features.expense.map((f, i) => ({ ...f, icon: expenseIcons[i] }));

  return (
    <section 
      id="funcionalidades" 
      className="py-20 lg:py-32 bg-[#FAFAFA]"
      data-testid="features-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Shopping Lists Block */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <h2 
              className="text-3xl sm:text-4xl font-bold text-[#111827] mb-4"
              style={{ fontFamily: 'Poppins, sans-serif' }}
              data-testid="features-shopping-title"
            >
              {t.features.shoppingTitle}
            </h2>
            <p className="text-[#6B7280] text-base sm:text-lg max-w-2xl mx-auto">
              {t.features.shoppingSubtitle}
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {shoppingFeatures.map((feature, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm card-hover"
                data-testid={`feature-card-shopping-${index}`}
              >
                <div className="feature-icon feature-icon-indigo mb-4">
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold text-[#111827] mb-2">
                  {feature.title}
                </h3>
                <p className="text-[#6B7280] text-sm">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Expense Control Block */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <h2 
              className="text-3xl sm:text-4xl font-bold text-[#111827] mb-4"
              style={{ fontFamily: 'Poppins, sans-serif' }}
              data-testid="features-expense-title"
            >
              {t.features.expenseTitle}
            </h2>
            <p className="text-[#6B7280] text-base sm:text-lg max-w-2xl mx-auto">
              {t.features.expenseSubtitle}
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {expenseFeatures.map((feature, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm card-hover"
                data-testid={`feature-card-expense-${index}`}
              >
                <div className="feature-icon feature-icon-emerald mb-4">
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold text-[#111827] mb-2">
                  {feature.title}
                </h3>
                <p className="text-[#6B7280] text-sm">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const { t } = useLang();
  const shoppingSteps = t.howItWorks.shoppingSteps.map((s, i) => ({ ...s, step: i + 1 }));
  const expenseSteps = t.howItWorks.expenseSteps.map((s, i) => ({ ...s, step: i + 1 }));

  return (
    <section 
      id="como-funciona" 
      className="py-20 lg:py-32 bg-white"
      data-testid="how-it-works-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <h2 
              className="text-3xl sm:text-4xl font-bold text-[#111827] mb-4"
              style={{ fontFamily: 'Poppins, sans-serif' }}
              data-testid="how-it-works-title"
            >
              {t.howItWorks.title}
            </h2>
          </motion.div>

          <motion.div variants={fadeInUp}>
            <Tabs defaultValue="expenses" className="w-full">
              <TabsList className="w-full max-w-md mx-auto mb-12 bg-gray-100 p-1 rounded-full">
                <TabsTrigger 
                  value="expenses" 
                  className="flex-1 rounded-full data-[state=active]:bg-[#4F46E5] data-[state=active]:text-white transition-all"
                  data-testid="tab-expenses"
                >
                  {t.howItWorks.tabExpenses}
                </TabsTrigger>
                <TabsTrigger 
                  value="shopping" 
                  className="flex-1 rounded-full data-[state=active]:bg-[#4F46E5] data-[state=active]:text-white transition-all"
                  data-testid="tab-shopping"
                >
                  {t.howItWorks.tabShopping}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="expenses" data-testid="tab-content-expenses">
                <div className="grid md:grid-cols-3 gap-8">
                  {expenseSteps.map((item) => (
                    <div key={item.step} className="text-center">
                      <div className="w-16 h-16 bg-[#4F46E5] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                        {item.step}
                      </div>
                      <h3 className="text-lg font-semibold text-[#111827] mb-2">
                        {item.title}
                      </h3>
                      <p className="text-[#6B7280] text-sm">
                        {item.description}
                      </p>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="shopping" data-testid="tab-content-shopping">
                <div className="grid md:grid-cols-3 gap-8">
                  {shoppingSteps.map((item) => (
                    <div key={item.step} className="text-center">
                      <div className="w-16 h-16 bg-[#059669] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                        {item.step}
                      </div>
                      <h3 className="text-lg font-semibold text-[#111827] mb-2">
                        {item.title}
                      </h3>
                      <p className="text-[#6B7280] text-sm">
                        {item.description}
                      </p>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = ({ onCtaClick }) => {
  const { t } = useLang();
  const [isAnnual, setIsAnnual] = useState(false);
  const plans = t.pricing.plans;

  return (
    <section 
      id="planos" 
      className="py-20 lg:py-32 bg-[#FAFAFA]"
      data-testid="pricing-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <h2 
              className="text-3xl sm:text-4xl font-bold text-[#111827] mb-4"
              style={{ fontFamily: 'Poppins, sans-serif' }}
              data-testid="pricing-title"
            >
              {t.pricing.title}
            </h2>
            <p className="text-[#6B7280] text-base sm:text-lg max-w-2xl mx-auto mb-8">
              {t.pricing.subtitle}
            </p>

            {/* Toggle */}
            <div className="flex items-center justify-center gap-4" data-testid="pricing-toggle">
              <span className={`font-medium ${!isAnnual ? 'text-[#111827]' : 'text-[#6B7280]'}`}>
                {t.pricing.monthly}
              </span>
              <Switch
                checked={isAnnual}
                onCheckedChange={setIsAnnual}
                className="data-[state=checked]:bg-[#4F46E5]"
                data-testid="pricing-toggle-switch"
              />
              <span className={`font-medium ${isAnnual ? 'text-[#111827]' : 'text-[#6B7280]'}`}>
                {t.pricing.annual}
              </span>
              {isAnnual && (
                <span className="bg-[#F59E0B] text-white text-xs font-semibold px-3 py-1 rounded-full">
                  {t.pricing.saveBadge}
                </span>
              )}
            </div>
          </motion.div>

          <motion.div variants={fadeInUp} className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <div
                key={plan.name}
                className={`pricing-card relative ${plan.isPro ? 'pricing-card-pro' : ''}`}
                data-testid={`pricing-card-${plan.name.toLowerCase()}`}
              >
                {plan.badge && (
                  <span className={`${plan.badge.color} text-white text-xs font-semibold px-3 py-1 rounded-full absolute -top-3 left-1/2 transform -translate-x-1/2`}>
                    {plan.badge.text}
                  </span>
                )}
                
                <h3 className="text-xl font-bold text-[#111827] mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {plan.name}
                </h3>
                
                <div className="mb-6">
                  <span className="text-3xl font-bold text-[#4F46E5]">
                    {isAnnual ? plan.price.annual : plan.price.monthly}
                  </span>
                  {isAnnual && plan.savingsText && (
                    <p className="text-[#059669] text-sm mt-1">({plan.savingsText})</p>
                  )}
                </div>

                <ul className="space-y-3 mb-6 flex-1">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-3">
                      {feature.included ? (
                        <Check className="w-5 h-5 text-[#059669] flex-shrink-0 mt-0.5" />
                      ) : (
                        <Minus className="w-5 h-5 text-[#6B7280] flex-shrink-0 mt-0.5" />
                      )}
                      <span className={feature.included ? 'text-[#111827]' : 'text-[#6B7280]'}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={onCtaClick}
                  className={`w-full rounded-full btn-scale ${
                    plan.isPro 
                      ? 'bg-[#4F46E5] hover:bg-[#4338CA] text-white' 
                      : 'bg-gray-100 hover:bg-gray-200 text-[#111827]'
                  }`}
                  data-testid={`pricing-btn-${plan.name.toLowerCase()}`}
                >
                  {plan.name === "Free" ? t.pricing.startFree : t.pricing.subscribe}
                </Button>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const { t } = useLang();
  const faqs = t.faq.items;

  return (
    <section
      id="faq"
      className="py-20 lg:py-32 bg-[#FAFAFA]"
      data-testid="faq-section"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          {/* Header */}
          <motion.div variants={fadeInUp} className="text-center mb-4">
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#111827] mb-4"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {t.faq.title}
            </h2>
            <p className="text-[#6B7280] text-base sm:text-lg">
              {t.faq.subtitle}
            </p>
          </motion.div>

          {/* FAQ Accordion */}
          <motion.div variants={fadeInUp}>
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-white border border-gray-100 rounded-2xl px-6 shadow-sm data-[state=open]:border-[#4F46E5]/30"
                >
                  <AccordionTrigger className="text-left text-sm font-semibold text-[#111827] hover:text-[#4F46E5] hover:no-underline py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#6B7280] text-sm leading-relaxed whitespace-pre-line pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>

          {/* Contact card bottom */}
          <motion.div variants={fadeInUp} className="mt-10 bg-white border border-gray-100 rounded-2xl p-6 shadow-sm text-center">
            <p className="font-semibold text-[#111827] mb-3">{t.faq.notFound}</p>
            <a
              href="mailto:contato@app-billy.com"
              className="inline-flex items-center gap-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-medium px-5 py-2.5 rounded-xl transition-colors btn-scale"
            >
              <Mail className="w-4 h-4" />
              {t.faq.emailSupport}
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Download Section
const DownloadSection = ({ onPlayClick, onAppleClick }) => {
  const { t } = useLang();
  return (
    <section 
      id="download" 
      className="py-20 lg:py-32 bg-white"
      data-testid="download-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center"
        >
          <motion.h2
            variants={fadeInUp}
            className="text-3xl sm:text-4xl font-bold text-[#111827] mb-4"
            style={{ fontFamily: 'Poppins, sans-serif' }}
            data-testid="download-title"
          >
            {t.download.title}
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-[#6B7280] text-base sm:text-lg max-w-2xl mx-auto mb-12"
          >
            {t.download.subtitle}
          </motion.p>

          <motion.div
            variants={fadeInUp}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <button
              onClick={onPlayClick}
              className="store-button store-button-play btn-scale"
              data-testid="download-play-store-btn"
            >
              <Play className="w-6 h-6" />
              <span>Google Play</span>
            </button>
            <button
              onClick={onAppleClick}
              className="store-button store-button-apple btn-scale"
              data-testid="download-app-store-btn"
            >
              <Apple className="w-6 h-6" />
              <span>App Store</span>
            </button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Footer
const Footer = ({ onPrivacyClick, onTermsClick }) => {
  const { t } = useLang();
  const socialIcons = [
    { icon: Instagram, label: "Instagram", href: "https://instagram.com/myapp.billy" },
    { icon: Mail, label: "E-mail", href: "mailto:contato@app-billy.com" }
  ];

  return (
    <footer className="bg-[#111827] text-white py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8 mb-12">
          {/* Logo & Description */}
          <div className="md:col-span-2">
            <span className="text-2xl font-bold text-[#4F46E5]" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Billy
            </span>
            <p className="text-gray-400 mt-4 max-w-md">
              {t.footer.description}
            </p>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-semibold mb-4">{t.footer.links}</h4>
            <ul className="space-y-2">
              <li>
                <a href="#privacy" onClick={onPrivacyClick} className="footer-link text-gray-400 hover:text-[#4F46E5] transition-colors" data-testid="footer-privacy">
                  {t.footer.privacy}
                </a>
              </li>
              <li>
                <a href="#terms" onClick={onTermsClick} className="footer-link text-gray-400 hover:text-[#4F46E5] transition-colors" data-testid="footer-terms">
                  {t.footer.terms}
                </a>
              </li>
              <li>
                <a href="#" className="footer-link text-gray-400 hover:text-[#4F46E5] transition-colors" data-testid="footer-contact">
                  {t.footer.contact}
                </a>
              </li>
            </ul>
          </div>

          {/* Contatos */}
          <div>
            <h4 className="font-semibold mb-4">{t.footer.contacts}</h4>
            <div className="flex gap-3 mb-4">
              {socialIcons.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-icon bg-white/10 hover:bg-[#4F46E5]"
                  aria-label={social.label}
                  data-testid={`footer-social-${social.label.toLowerCase()}`}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
            <a
              href="mailto:contato@app-billy.com"
              className="flex items-center gap-2 text-gray-400 hover:text-[#4F46E5] transition-colors text-sm"
              data-testid="footer-email"
            >
              <Mail className="w-4 h-4" />
              contato@app-billy.com
            </a>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-gray-800 pt-8 text-center">
          <p className="text-gray-500 text-sm" data-testid="footer-copyright">
            {t.footer.copyright}
          </p>
        </div>
      </div>
    </footer>
  );
};

// Google Play Modal
const PlayStoreModal = ({ isOpen, onClose }) => {
  const { t } = useLang();
  const [email, setEmail] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email) {
      toast.success(t.modals.playStore.successMessage);
      setEmail("");
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="play-store-modal">
        <DialogHeader>
          <DialogTitle className="text-center text-[#059669]" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {t.modals.playStore.title}
          </DialogTitle>
          <DialogDescription className="text-center">
            {t.modals.playStore.description}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="flex gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.modals.playStore.placeholder}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#059669] focus:border-transparent"
              required
              data-testid="play-store-email-input"
            />
            <Button
              type="submit"
              className="bg-[#059669] hover:bg-[#047857] text-white rounded-lg btn-scale"
              data-testid="play-store-submit-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              {t.modals.playStore.button}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Terms of Use Modal
const TermsOfUseModal = ({ isOpen, onClose }) => {
  const sections = {
    pt: {
      title: "Termos de Uso",
      updated: "\u00daltima atualiza\u00e7\u00e3o: 04 de fevereiro de 2026",
      content: [
        { heading: "1. Aceita\u00e7\u00e3o dos Termos", body: "Ao acessar e usar o Billy (\u201cServi\u00e7o\u201d), voc\u00ea concorda em cumprir e estar vinculado a estes Termos de Uso. Se voc\u00ea n\u00e3o concordar com qualquer parte destes termos, n\u00e3o use nosso servi\u00e7o.\n\nEstes termos aplicam-se a todos os usu\u00e1rios do aplicativo, incluindo usu\u00e1rios gratuitos e pagos." },
        { heading: "2. Descri\u00e7\u00e3o do Servi\u00e7o", body: "O Billy \u00e9 um aplicativo de gerenciamento de despesas que permite:\n\n\u2022 Criar e gerenciar listas de despesas.\n\u2022 Adicionar itens com pre\u00e7os e quantidades.\n\u2022 Compartilhar despesas com outros usu\u00e1rios.\n\u2022 Fazer upload de comprovantes.\n\u2022 Visualizar estat\u00edsticas e relat\u00f3rios de gastos.\n\nO servi\u00e7o \u00e9 fornecido em tr\u00eas planos: FREE, ESSENTIAL e PRO, cada um com recursos espec\u00edficos." },
        { heading: "3. Registro e Conta", body: "Para usar o Billy, voc\u00ea deve:\n\n\u2022 Ter pelo menos 13 anos de idade.\n\u2022 Fornecer informa\u00e7\u00f5es precisas e completas durante o registro.\n\u2022 Manter a seguran\u00e7a de sua senha.\n\u2022 Notificar-nos imediatamente sobre qualquer uso n\u00e3o autorizado.\n\u2022 Ser respons\u00e1vel por todas as atividades em sua conta.\n\nVoc\u00ea n\u00e3o pode compartilhar sua conta com terceiros ou criar m\u00faltiplas contas para burlar limita\u00e7\u00f5es do plano." },
        { heading: "4. Planos e Pagamentos", body: "Plano FREE:\n\u2022 3 despesas simult\u00e2neas\n\u2022 Compartilhar com 1 pessoa\n\u2022 Hist\u00f3rico de 3 meses\n\nPlano ESSENTIAL:\n\u2022 Despesas ilimitadas\n\u2022 Compartilhar com 4 pessoas\n\u2022 Comprovantes\n\u2022 Hist\u00f3rico de 1 ano\n\nPlano PRO:\n\u2022 Todos os recursos ESSENTIAL\n\u2022 Compartilhamento ilimitado\n\u2022 Hist\u00f3rico ilimitado\n\u2022 Metas mensais\n\u2022 Analytics avan\u00e7ados\n\nPagamentos s\u00e3o processados atrav\u00e9s de plataformas de terceiros. Voc\u00ea \u00e9 respons\u00e1vel por todos os encargos associados." },
        { heading: "5. Conte\u00fado do Usu\u00e1rio", body: "Voc\u00ea mant\u00e9m todos os direitos sobre o conte\u00fado que cria. Ao usar o servi\u00e7o, voc\u00ea nos concede uma licen\u00e7a limitada para:\n\n\u2022 Armazenar e processar seu conte\u00fado.\n\u2022 Exibir seu conte\u00fado para usu\u00e1rios com quem voc\u00ea compartilha.\n\u2022 Fazer backup e recupera\u00e7\u00e3o de dados.\n\nVoc\u00ea n\u00e3o deve:\n\u2022 Compartilhar conte\u00fado ilegal ou ofensivo.\n\u2022 Violar direitos de propriedade intelectual.\n\u2022 Fazer upload de malware ou c\u00f3digo malicioso." },
        { heading: "6. Uso Aceit\u00e1vel", body: "Voc\u00ea concorda em N\u00c3O:\n\n\u2022 Usar o servi\u00e7o para fins ilegais.\n\u2022 Tentar obter acesso n\u00e3o autorizado ao sistema.\n\u2022 Interferir ou interromper o servi\u00e7o.\n\u2022 Fazer engenharia reversa do aplicativo.\n\u2022 Usar bots ou scripts automatizados.\n\u2022 Coletar dados de outros usu\u00e1rios sem permiss\u00e3o.\n\u2022 Revender ou redistribuir o servi\u00e7o.\n\nViol\u00e7\u00f5es podem resultar em suspens\u00e3o ou encerramento da conta." },
        { heading: "7. Propriedade Intelectual", body: "O Billy e todo o seu conte\u00fado (design, c\u00f3digo, logo, textos) s\u00e3o propriedade exclusiva da nossa empresa e protegidos por leis de propriedade intelectual.\n\nA licen\u00e7a concedida n\u00e3o permite:\n\u2022 Copiar ou modificar o software.\n\u2022 Criar trabalhos derivados.\n\u2022 Remover marcas ou avisos de copyright." },
        { heading: "8. Garantias e Responsabilidade", body: "O servi\u00e7o \u00e9 fornecido \u201ccomo est\u00e1\u201d sem garantias de qualquer tipo. N\u00e3o garantimos que o servi\u00e7o ser\u00e1 ininterrupto, livre de erros ou que defeitos ser\u00e3o corrigidos.\n\nN\u00e3o somos respons\u00e1veis por:\n\u2022 Perda de dados ou lucros.\n\u2022 Danos indiretos ou consequenciais.\n\u2022 Falhas de terceiros (hospedagem, internet).\n\nNossa responsabilidade total n\u00e3o exceder\u00e1 o valor pago por voc\u00ea nos \u00faltimos 12 meses." },
        { heading: "9. Compartilhamento de Despesas", body: "Ao compartilhar uma despesa:\n\n\u2022 Voc\u00ea autoriza outros usu\u00e1rios a visualizar e editar a despesa.\n\u2022 Voc\u00ea pode remover o compartilhamento a qualquer momento.\n\u2022 Usu\u00e1rios compartilhados n\u00e3o podem excluir a despesa (apenas o propriet\u00e1rio).\n\u2022 Voc\u00ea \u00e9 respons\u00e1vel por gerenciar permiss\u00f5es de compartilhamento." },
        { heading: "10. Encerramento", body: "Voc\u00ea pode encerrar sua conta a qualquer momento pelas configura\u00e7\u00f5es do aplicativo.\n\nPodemos suspender ou encerrar sua conta se:\n\u2022 Voc\u00ea violar estes termos.\n\u2022 Detectarmos atividade fraudulenta.\n\u2022 Voc\u00ea n\u00e3o pagar por servi\u00e7os contratados.\n\u2022 For exigido por lei.\n\nAp\u00f3s o encerramento, seus dados ser\u00e3o deletados conforme nossa Pol\u00edtica de Privacidade. Assinaturas pagas n\u00e3o ser\u00e3o reembolsadas (salvo determina\u00e7\u00e3o legal)." },
        { heading: "11. Modifica\u00e7\u00f5es do Servi\u00e7o", body: "Reservamos o direito de modificar ou descontinuar o servi\u00e7o, alterar pre\u00e7os (com 30 dias de aviso pr\u00e9vio), adicionar ou remover recursos e atualizar estes termos.\n\nContinuar usando o servi\u00e7o ap\u00f3s altera\u00e7\u00f5es constitui aceita\u00e7\u00e3o dos novos termos." },
        { heading: "12. Lei Aplic\u00e1vel", body: "Estes termos s\u00e3o regidos pelas leis do Brasil. Quaisquer disputas ser\u00e3o resolvidas nos tribunais competentes.\n\nSe qualquer disposi\u00e7\u00e3o destes termos for considerada inv\u00e1lida, as demais disposi\u00e7\u00f5es permanecer\u00e3o em vigor." },
        { heading: "13. Contato", body: "Para quest\u00f5es sobre estes Termos de Uso:\n\nE-mail: contato@app-billy.com\n\nResponderemos dentro de 5 dias \u00fateis." }
      ]
    },
    en: {
      title: "Terms of Service",
      updated: "Last updated: February 04, 2026",
      content: [
        { heading: "1. Acceptance of Terms", body: "By accessing and using Billy (\u201cService\u201d), you agree to comply with and be bound by these Terms of Service. If you do not agree with any part of these terms, do not use our service.\n\nThese terms apply to all users of the application, including free and paid users." },
        { heading: "2. Service Description", body: "Billy is an expense management application that allows:\n\n\u2022 Create and manage expense lists.\n\u2022 Add items with prices and quantities.\n\u2022 Share expenses with other users.\n\u2022 Upload receipts.\n\u2022 View expense statistics and reports.\n\nThe service is provided in three plans: FREE, ESSENTIAL, and PRO, each with specific features." },
        { heading: "3. Registration and Account", body: "To use Billy, you must:\n\n\u2022 Be at least 13 years old.\n\u2022 Provide accurate and complete information during registration.\n\u2022 Maintain the security of your password.\n\u2022 Notify us immediately of any unauthorized use.\n\u2022 Be responsible for all activities on your account.\n\nYou may not share your account with third parties or create multiple accounts to circumvent plan limitations." },
        { heading: "4. Plans and Payments", body: "FREE Plan:\n\u2022 3 simultaneous expenses\n\u2022 Share with 1 person\n\u2022 3-month history\n\nESSENTIAL Plan:\n\u2022 Unlimited expenses\n\u2022 Share with 4 people\n\u2022 Receipts\n\u2022 1-year history\n\nPRO Plan:\n\u2022 All ESSENTIAL features\n\u2022 Unlimited sharing\n\u2022 Unlimited history\n\u2022 Monthly goals\n\u2022 Advanced analytics\n\nPayments are processed through third-party platforms. You are responsible for all associated charges." },
        { heading: "5. User Content", body: "You retain all rights to the content you create. By using the service, you grant us a limited license to:\n\n\u2022 Store and process your content.\n\u2022 Display your content to users you share with.\n\u2022 Backup and recover data.\n\nYou must not:\n\u2022 Share illegal or offensive content.\n\u2022 Violate intellectual property rights.\n\u2022 Upload malware or malicious code." },
        { heading: "6. Acceptable Use", body: "You agree NOT to:\n\n\u2022 Use the service for illegal purposes.\n\u2022 Attempt unauthorized access to the system.\n\u2022 Interfere with or disrupt the service.\n\u2022 Reverse engineer the application.\n\u2022 Use bots or automated scripts.\n\u2022 Collect data from other users without permission.\n\u2022 Resell or redistribute the service.\n\nViolations may result in account suspension or termination." },
        { heading: "7. Intellectual Property", body: "Billy and all its content (design, code, logo, text) are the exclusive property of our company and protected by intellectual property laws.\n\nThe license granted does not allow:\n\u2022 Copying or modifying the software.\n\u2022 Creating derivative works.\n\u2022 Removing trademarks or copyright notices." },
        { heading: "8. Warranties and Liability", body: "The service is provided \u201cas is\u201d without warranties of any kind. We do not guarantee the service will be uninterrupted, error-free, or that defects will be corrected.\n\nWe are not responsible for:\n\u2022 Loss of data or profits.\n\u2022 Indirect or consequential damages.\n\u2022 Third-party failures (hosting, internet).\n\nOur total liability will not exceed the amount paid by you in the last 12 months." },
        { heading: "9. Expense Sharing", body: "When sharing an expense:\n\n\u2022 You authorize other users to view and edit the expense.\n\u2022 You can remove sharing at any time.\n\u2022 Shared users cannot delete the expense (owner only).\n\u2022 You are responsible for managing sharing permissions." },
        { heading: "10. Termination", body: "You may terminate your account at any time through app settings.\n\nWe may suspend or terminate your account if:\n\u2022 You violate these terms.\n\u2022 We detect fraudulent activity.\n\u2022 You fail to pay for contracted services.\n\u2022 Required by law.\n\nAfter termination, your data will be deleted according to our Privacy Policy. Paid subscriptions will not be refunded (except as required by law)." },
        { heading: "11. Service Modifications", body: "We reserve the right to modify or discontinue the service, change prices (with 30 days notice), add or remove features, and update these terms.\n\nContinued use of the service after changes constitutes acceptance of the new terms." },
        { heading: "12. Governing Law", body: "These terms are governed by the laws of Brazil. Any disputes will be resolved in the competent courts.\n\nIf any provision of these terms is found invalid, the remaining provisions will remain in effect." },
        { heading: "13. Contact", body: "For questions about these Terms of Service:\n\nEmail: contato@app-billy.com\n\nWe will respond within 5 business days." }
      ]
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col" data-testid="terms-modal">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-[#4F46E5] text-xl" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Termos de Uso
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="pt" className="flex flex-col flex-1 overflow-hidden">
          <TabsList className="w-full max-w-xs mx-auto mb-4 bg-gray-100 p-1 rounded-full">
            <TabsTrigger value="pt" className="flex-1 rounded-full data-[state=active]:bg-[#4F46E5] data-[state=active]:text-white transition-all">
              🇧🇷 Português
            </TabsTrigger>
            <TabsTrigger value="en" className="flex-1 rounded-full data-[state=active]:bg-[#4F46E5] data-[state=active]:text-white transition-all">
              🇺🇸 English
            </TabsTrigger>
          </TabsList>

          {Object.entries(sections).map(([lang, data]) => (
            <TabsContent key={lang} value={lang} className="flex-1 overflow-hidden mt-0">
              <ScrollArea className="h-[55vh] pr-4">
                <p className="text-xs text-gray-400 mb-6">{data.updated}</p>
                <div className="space-y-6">
                  {data.content.map((section) => (
                    <div key={section.heading}>
                      <h3 className="font-semibold text-[#111827] mb-2 text-sm">{section.heading}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">{section.body}</p>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </TabsContent>
          ))}
        </Tabs>

        <div className="flex justify-end pt-4 border-t border-gray-100">
          <Button
            onClick={onClose}
            className="bg-[#4F46E5] hover:bg-[#4338CA] text-white rounded-lg btn-scale"
            data-testid="terms-modal-close-btn"
          >
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Privacy Policy Modal
const PrivacyPolicyModal = ({ isOpen, onClose }) => {
  const sections = {
    pt: {
      title: "Política de Privacidade",
      updated: "Última atualização: 04 de fevereiro de 2026",
      content: [
        { heading: "1. Introdução", body: "Bem-vindo ao Billy. Esta Política de Privacidade explica como coletamos, usamos, divulgamos e protegemos suas informações quando você usa nosso aplicativo móvel. Ao usar o Billy, você concorda com a coleta e uso de informações de acordo com esta política." },
        { heading: "2. Informações que Coletamos", body: "Coletamos diferentes tipos de informações para fornecer e melhorar nosso serviço:\n\n• Informações de Conta: nome, endereço de e-mail e senha (criptografada).\n• Dados de Despesas: listas de despesas, itens, preços e comprovantes que você criar.\n• Dados de Compartilhamento: informações sobre com quem você compartilha suas despesas.\n• Dados de Uso: como você interage com o aplicativo, incluindo recursos usados e tempo de utilização.\n• Informações do Dispositivo: tipo de dispositivo, sistema operacional, identificadores únicos e dados de diagnóstico." },
        { heading: "3. Como Usamos Suas Informações", body: "Utilizamos as informações coletadas para:\n\n• Fornecer, manter e melhorar nossos serviços.\n• Processar suas despesas e sincronizar dados entre dispositivos.\n• Permitir compartilhamento de despesas com outros usuários.\n• Enviar notificações sobre atualizações e alterações em despesas compartilhadas.\n• Detectar, prevenir e resolver problemas técnicos.\n• Cumprir obrigações legais e proteger nossos direitos." },
        { heading: "4. Compartilhamento de Informações", body: "Não vendemos suas informações pessoais. Podemos compartilhar suas informações apenas:\n\n• Com outros usuários quando você compartilha uma despesa.\n• Com provedores de serviços terceiros que nos ajudam a operar o aplicativo (hospedagem, analytics).\n• Quando exigido por lei ou para proteger nossos direitos.\n• Em caso de fusão, aquisição ou venda de ativos (com notificação prévia)." },
        { heading: "5. Armazenamento e Segurança", body: "Seus dados são armazenados em servidores seguros com criptografia. Implementamos medidas de segurança técnicas e organizacionais para proteger suas informações contra acesso não autorizado, alteração, divulgação ou destruição.\n\nSuas senhas são criptografadas usando algoritmos de hash seguros. Comprovantes são armazenados com criptografia em repouso." },
        { heading: "6. Seus Direitos", body: "Você tem o direito de:\n\n• Acessar suas informações pessoais.\n• Corrigir informações imprecisas.\n• Solicitar a exclusão de sua conta e dados.\n• Exportar seus dados em formato legível.\n• Retirar consentimento para processamento de dados.\n\nPara exercer esses direitos, entre em contato: contato@app-billy.com" },
        { heading: "7. Retenção de Dados", body: "Mantemos suas informações pelo tempo necessário para fornecer o serviço e cumprir nossas obrigações legais:\n\n• Dados de conta: mantidos enquanto sua conta estiver ativa.\n• Dados de despesas: conforme seu plano (3 meses FREE, 1 ano ESSENTIAL, ilimitado PRO).\n• Dados de pagamento: 7 anos para fins fiscais.\n• Após exclusão da conta: dados anonimizados ou deletados em até 30 dias." },
        { heading: "8. Privacidade de Menores", body: "Nosso serviço não é direcionado a menores de 13 anos. Não coletamos intencionalmente informações pessoais de crianças. Se você é pai ou responsável e sabe que seu filho nos forneceu informações, entre em contato conosco." },
        { heading: "9. Cookies e Tecnologias Similares", body: "Usamos tecnologias como cookies e tokens de autenticação para:\n\n• Manter você conectado ao aplicativo.\n• Lembrar suas preferências (idioma, tema).\n• Analisar padrões de uso para melhorar o serviço.\n\nVocê pode gerenciar preferências de cookies nas configurações do seu dispositivo." },
        { heading: "10. Transferências Internacionais", body: "Seus dados podem ser transferidos e mantidos em computadores localizados fora do seu país, onde as leis de proteção de dados podem ser diferentes. Garantimos que tais transferências cumpram as leis aplicáveis e que seus dados permaneçam protegidos." },
        { heading: "11. Alterações nesta Política", body: "Podemos atualizar nossa Política de Privacidade periodicamente. Notificaremos você sobre quaisquer alterações publicando a nova política nesta página e atualizando a data de \"última atualização\".\n\nAlterações significativas serão comunicadas por e-mail ou notificação no aplicativo." },
        { heading: "12. Contato", body: "Se você tiver dúvidas sobre esta Política de Privacidade, entre em contato:\n\nE-mail: contato@app-billy.com\n\nResponderemos a todas as solicitações dentro de 30 dias." }
      ]
    },
    en: {
      title: "Privacy Policy",
      updated: "Last updated: February 04, 2026",
      content: [
        { heading: "1. Introduction", body: "Welcome to Billy. This Privacy Policy explains how we collect, use, disclose, and protect your information when you use our mobile application. By using Billy, you agree to the collection and use of information in accordance with this policy." },
        { heading: "2. Information We Collect", body: "We collect different types of information to provide and improve our service:\n\n• Account Information: name, email address, and password (encrypted).\n• Expense Data: expense lists, items, prices, and receipts you create.\n• Sharing Data: information about who you share your expenses with.\n• Usage Data: how you interact with the app, including features used and time spent.\n• Device Information: device type, operating system, unique identifiers, and diagnostic data." },
        { heading: "3. How We Use Your Information", body: "We use the collected information to:\n\n• Provide, maintain, and improve our services.\n• Process your expenses and sync data across devices.\n• Enable expense sharing with other users.\n• Send notifications about updates and changes to shared expenses.\n• Detect, prevent, and address technical issues.\n• Comply with legal obligations and protect our rights." },
        { heading: "4. Information Sharing", body: "We do not sell your personal information. We may share your information only:\n\n• With other users when you share an expense.\n• With third-party service providers who help us operate the app (hosting, analytics).\n• When required by law or to protect our rights.\n• In case of merger, acquisition, or asset sale (with prior notice)." },
        { heading: "5. Storage and Security", body: "Your data is stored on secure servers with encryption. We implement technical and organizational security measures to protect your information against unauthorized access, alteration, disclosure, or destruction.\n\nYour passwords are encrypted using secure hashing algorithms. Receipts are stored with encryption at rest." },
        { heading: "6. Your Rights", body: "You have the right to:\n\n• Access your personal information.\n• Correct inaccurate information.\n• Request deletion of your account and data.\n• Export your data in a readable format.\n• Withdraw consent for data processing.\n\nTo exercise these rights, contact us at: contato@app-billy.com" },
        { heading: "7. Data Retention", body: "We keep your information as long as necessary to provide the service and comply with our legal obligations:\n\n• Account data: kept while your account is active.\n• Expense data: according to your plan (3 months FREE, 1 year ESSENTIAL, unlimited PRO).\n• Payment data: 7 years for tax purposes.\n• After account deletion: data anonymized or deleted within 30 days." },
        { heading: "8. Children's Privacy", body: "Our service is not directed to children under 13. We do not knowingly collect personal information from children. If you are a parent or guardian and know that your child has provided us with information, please contact us." },
        { heading: "9. Cookies and Similar Technologies", body: "We use technologies like cookies and authentication tokens to:\n\n• Keep you logged into the app.\n• Remember your preferences (language, theme).\n• Analyze usage patterns to improve the service.\n\nYou can manage cookie preferences in your device settings." },
        { heading: "10. International Transfers", body: "Your data may be transferred to and maintained on computers located outside your country, where data protection laws may differ. We ensure such transfers comply with applicable laws and that your data remains protected." },
        { heading: "11. Changes to This Policy", body: "We may update our Privacy Policy periodically. We will notify you of any changes by posting the new policy on this page and updating the \"last updated\" date.\n\nSignificant changes will be communicated via email or in-app notification." },
        { heading: "12. Contact", body: "If you have questions about this Privacy Policy, contact us:\n\nEmail: contato@app-billy.com\n\nWe will respond to all requests within 30 days." }
      ]
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col" data-testid="privacy-modal">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-[#4F46E5] text-xl" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Política de Privacidade
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="pt" className="flex flex-col flex-1 overflow-hidden">
          <TabsList className="w-full max-w-xs mx-auto mb-4 bg-gray-100 p-1 rounded-full">
            <TabsTrigger value="pt" className="flex-1 rounded-full data-[state=active]:bg-[#4F46E5] data-[state=active]:text-white transition-all">
              🇧🇷 Português
            </TabsTrigger>
            <TabsTrigger value="en" className="flex-1 rounded-full data-[state=active]:bg-[#4F46E5] data-[state=active]:text-white transition-all">
              🇺🇸 English
            </TabsTrigger>
          </TabsList>

          {Object.entries(sections).map(([lang, data]) => (
            <TabsContent key={lang} value={lang} className="flex-1 overflow-hidden mt-0">
              <ScrollArea className="h-[55vh] pr-4">
                <p className="text-xs text-gray-400 mb-6">{data.updated}</p>
                <div className="space-y-6">
                  {data.content.map((section) => (
                    <div key={section.heading}>
                      <h3 className="font-semibold text-[#111827] mb-2 text-sm">{section.heading}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">{section.body}</p>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </TabsContent>
          ))}
        </Tabs>

        <div className="flex justify-end pt-4 border-t border-gray-100">
          <Button
            onClick={onClose}
            className="bg-[#4F46E5] hover:bg-[#4338CA] text-white rounded-lg btn-scale"
            data-testid="privacy-modal-close-btn"
          >
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Subscribe Modal
const SubscribeModal = ({ isOpen, onClose }) => {
  const { t } = useLang();
  const [email, setEmail] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email) {
      toast.success(t.modals.subscribe.successMessage);
      setEmail("");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="subscribe-modal">
        <DialogHeader>
          <DialogTitle className="text-center text-[#4F46E5]" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {t.modals.subscribe.title}
          </DialogTitle>
          <DialogDescription className="text-center">
            {t.modals.subscribe.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* iOS — direct App Store link */}
          <a
            href="https://apps.apple.com/us/app/billy/id6760142702"
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
            className="flex items-center justify-center gap-2 w-full bg-[#4F46E5] hover:bg-[#4338CA] text-white font-medium py-2.5 px-4 rounded-lg btn-scale transition-colors"
            data-testid="subscribe-ios-btn"
          >
            <Apple className="w-5 h-5" />
            {t.modals.subscribe.iosButton}
          </a>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-gray-400">{t.modals.subscribe.androidLabel}</span>
            </div>
          </div>

          {/* Android — email notification */}
          <form onSubmit={handleSubmit} className="flex gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.modals.subscribe.placeholder}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#059669] focus:border-transparent text-sm"
              required
              data-testid="subscribe-android-email"
            />
            <Button
              type="submit"
              className="bg-[#059669] hover:bg-[#047857] text-white rounded-lg btn-scale flex items-center gap-1"
              data-testid="subscribe-android-btn"
            >
              <Play className="w-4 h-4" />
              <span>{t.modals.subscribe.notifyButton}</span>
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main App Component
function App() {
  const [playModalOpen, setPlayModalOpen] = useState(false);
  const [subscribeModalOpen, setSubscribeModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [lang, setLang] = useState("pt");

  const handleAppleClick = () => {
    window.open('https://apps.apple.com/us/app/billy/id6760142702', '_blank', 'noopener,noreferrer');
  };

  // Open modals based on URL hash
  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash;
      if (hash === "#terms") {
        setTermsModalOpen(true);
        setPrivacyModalOpen(false);
      } else if (hash === "#privacy") {
        setPrivacyModalOpen(true);
        setTermsModalOpen(false);
      }
    };
    handleHash();
    window.addEventListener("hashchange", handleHash);
    return () => window.removeEventListener("hashchange", handleHash);
  }, []);

  const handleCloseTerms = () => {
    setTermsModalOpen(false);
    if (window.location.hash === "#terms") history.pushState("", document.title, window.location.pathname + window.location.search);
  };

  const handleClosePrivacy = () => {
    setPrivacyModalOpen(false);
    if (window.location.hash === "#privacy") history.pushState("", document.title, window.location.pathname + window.location.search);
  };

  return (
    <LangContext.Provider value={{ lang, setLang, t: translations[lang] }}>
    <div className="min-h-screen" data-testid="app-root">
      <Toaster position="top-center" richColors />
      <Navbar />
      <HeroSection 
        onPlayClick={() => setPlayModalOpen(true)} 
        onAppleClick={handleAppleClick} 
      />
      <FeaturesSection />
      <HowItWorksSection />
      <PricingSection onCtaClick={() => setSubscribeModalOpen(true)} />
      <DownloadSection 
        onPlayClick={() => setPlayModalOpen(true)} 
        onAppleClick={handleAppleClick} 
      />
      <FAQSection />
      <Footer onPrivacyClick={() => setPrivacyModalOpen(true)} onTermsClick={() => setTermsModalOpen(true)} />

      {/* Modals */}
      <PlayStoreModal isOpen={playModalOpen} onClose={() => setPlayModalOpen(false)} />
      <SubscribeModal isOpen={subscribeModalOpen} onClose={() => setSubscribeModalOpen(false)} />
      <TermsOfUseModal isOpen={termsModalOpen} onClose={handleCloseTerms} />
      <PrivacyPolicyModal isOpen={privacyModalOpen} onClose={handleClosePrivacy} />
    </div>
    </LangContext.Provider>
  );
}

export default App;
